import { useState } from 'react';

import { Chip, Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import {
  Confirm,
  FunctionField,
  SimpleShowLayout,
  TextField,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { FileDetails, MEditButton, FileUploadButton } from 'src/lib';
import {
  AccountApplicationCategory,
  AccountApplicationCategoryName,
  AccountApplicationDocument,
  EntityAccountType,
} from 'src/models';
import { BaseStyle, Spacing } from 'src/styles';
import { findFileType } from 'src/utils';
import { getAccountApplicationGovernmentIdCategoryTitle } from 'src/utils/accountApplication.util';

import { EditApplicationCategoryModal } from './EditApplicationCategoryModal';
import { EditApplicationDocumentModal } from './EditApplicationDocumentModal';
import { UploadApplicationDocumentModal } from './UploadApplicationDocumentModal';

interface ApplicationCategoryProps {
  applicationId?: string;
  accountId?: string;
  category: AccountApplicationCategory;
  entityAccountType?: EntityAccountType;
}

export const ApplicationCategory = ({
  applicationId,
  accountId,
  category,
  entityAccountType,
}: ApplicationCategoryProps) => {
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editDocumentId, setEditDocumentId] = useState<string | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);

  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const canUploadDocument = () => {
    if (category.name.isGovernmentId) {
      return true;
    }

    return isEmpty(category.uploadedDocuments);
  };

  const getDocumentName = ({
    categoryName,
    document,
    index,
  }: {
    categoryName: AccountApplicationCategoryName;
    document: AccountApplicationDocument;
    index: number;
  }): string => {
    if (categoryName.isGovernmentId) {
      const title = getAccountApplicationGovernmentIdCategoryTitle({
        entityAccountType,
        index,
      });

      return [title, document.type.label, `(${document.originalFilename})`].join(' ');
    }

    return `${document.type.label}, (${document.originalFilename})`;
  };

  const onDocumentDelete = (id: string) => {
    setDeleteDocumentId(id);
    setIsDeleteConfirmOpen(true);
  };

  const onDocumentEdit = (id: string) => {
    setEditDocumentId(id);
    setIsEditOpen(true);
  };

  return (
    <Grid container>
      <Grid item md={12} direction='row' className={BaseStyle.levelLeft}>
        <Typography variant='h6' className={Spacing.mr16}>
          {category.name.label}
        </Typography>
        <FunctionField
          record={category}
          source='isResolved'
          render={(record: AccountApplicationCategory) => (
            <Chip
              label={record.isResolved ? 'Resolved' : 'Unresolved'}
              color={record.isResolved ? 'success' : 'error'}
              className={Spacing.mr16}
            />
          )}
        />
        <FileUploadButton
          onClick={() => setIsUploadOpen(true)}
          className={Spacing.mr8}
          disabled={!canUploadDocument()}
        />
        <MEditButton onClick={() => setIsEditCategoryOpen(true)} />
      </Grid>
      <Grid item md={12}>
        <Grid container>
          <Grid item md={4}>
            <SimpleShowLayout>
              <TextField
                record={{ requestedDocuments: category.requestedDocuments.map(anItem => anItem.label).join(', ') }}
                source='requestedDocuments'
                label='Requested Documents'
              />
            </SimpleShowLayout>
          </Grid>
          <Grid item md={4}>
            <SimpleShowLayout>
              <TextField record={category} source='adminNote' label='Note' />
            </SimpleShowLayout>
          </Grid>
          {category.name.isGovernmentId && (
            <Grid item md={4}>
              <SimpleShowLayout>
                <TextField record={category} source='adminNote' label='Total Requested Documents' />
              </SimpleShowLayout>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item md={12}>
        <SimpleShowLayout>
          <Typography variant='h6'>Uploaded Documents</Typography>
        </SimpleShowLayout>
      </Grid>
      {category.uploadedDocuments?.map((document, index) => (
        <FileDetails
          key={document.id}
          id={document.id}
          name={getDocumentName({ document, index, categoryName: category.name })}
          resource={{
            name: DataProviderResource.DownloadAccountApplicationDocument,
            params: {
              id: applicationId,
              meta: {
                accountId,
                documentId: document.id,
                name: document.originalFilename,
                type: findFileType(document.originalFilename),
              },
            },
          }}
          onEdit={onDocumentEdit}
          onDelete={onDocumentDelete}
        />
      ))}
      {isEditCategoryOpen && applicationId && accountId && (
        <EditApplicationCategoryModal
          applicationId={applicationId}
          accountId={accountId}
          isOpen={isEditCategoryOpen}
          onClose={() => setIsEditCategoryOpen(false)}
          category={category}
        />
      )}
      {isUploadOpen && applicationId && accountId && (
        <UploadApplicationDocumentModal
          applicationId={applicationId}
          accountId={accountId}
          isOpen={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          category={category}
        />
      )}
      {isEditOpen && applicationId && accountId && editDocumentId && (
        <EditApplicationDocumentModal
          applicationId={applicationId}
          accountId={accountId}
          documentId={editDocumentId}
          category={category}
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
        />
      )}
      <Confirm
        isOpen={isDeleteConfirmOpen}
        loading={isDeleteLoading}
        title={'Delete Document'}
        content={'Are you sure you want to delete the document?'}
        onConfirm={() => {
          if (!deleteDocumentId) {
            notify('No document selected for deletion', { type: 'warning' });

            return;
          }
          setIsDeleteLoading(true);
          dataProvider
            .delete(DataProviderResource.DeleteAccountApplicationDocument, {
              id: applicationId,
              meta: { accountId, documentId: deleteDocumentId },
            })
            .then(() => {
              notify('Document was successfully deleted.', {
                type: 'success',
                autoHideDuration: 7500,
              });
              refresh();
            })
            .catch(error => {
              notify(`Error deleting document. Received ${JSON.stringify(error)}`, {
                type: 'error',
              });
            })
            .finally(() => {
              setIsDeleteConfirmOpen(false);
              setIsDeleteLoading(false);
            });
        }}
        onClose={() => setIsDeleteConfirmOpen(false)}
      />
    </Grid>
  );
};
